// router.js — History API + a 30-line matcher.
//
// Routes are simple path patterns with :param capture and an optional ":rest*"
// catch-all. The matched component is rendered into the host element passed
// to start(). Navigation goes through navigate() so we can intercept link
// clicks and back/forward without page reloads.
//
// Identical body to KFE web's router.js — kept in sync deliberately.

/**
 * @typedef {object} Route
 * @property {string} path                   e.g. "/list/:listId"
 * @property {string} tag                    custom element tag to mount
 * @property {(params: Record<string,string>) => Record<string,string>} [props]
 */

/** @type {Route[]} */ let routes = [];
/** @type {HTMLElement | null} */ let host = null;

/** @param {Route[]} rs @param {HTMLElement} h */
export function start(rs, h) {
  routes = rs;
  host = h;
  window.addEventListener("popstate", render);
  document.addEventListener("click", onLinkClick);
  render();
}

/** @param {string} path */
export function navigate(path) {
  if (path === location.pathname) return;
  history.pushState({}, "", path);
  window.dispatchEvent(new Event("rm:routechange"));
  render();
}

function render() {
  if (!host) return;
  const path = location.pathname || "/";
  for (const r of routes) {
    const params = match(r.path, path);
    if (params) {
      const el = document.createElement(r.tag);
      const props = r.props ? r.props(params) : params;
      for (const [k, v] of Object.entries(props)) /** @type {any} */ (el)[k] = v;
      host.replaceChildren(el);
      return;
    }
  }
  host.replaceChildren(Object.assign(document.createElement("p"), { textContent: "Not found: " + path }));
}

/**
 * @param {string} pattern
 * @param {string} path
 * @returns {Record<string,string> | null}
 */
function match(pattern, path) {
  const pp = pattern.replace(/\/$/, "").split("/");
  const ap = path.replace(/\/$/, "").split("/");
  /** @type {Record<string,string>} */
  const params = {};
  for (let i = 0; i < pp.length; i++) {
    const p = pp[i];
    const a = ap[i];
    if (p?.startsWith(":")) {
      if (p.endsWith("*")) {
        params[p.slice(1, -1)] = ap.slice(i).join("/");
        return params;
      }
      if (a == null) return null;
      params[p.slice(1)] = decodeURIComponent(a);
    } else if (p !== a) {
      return null;
    }
  }
  return ap.length === pp.length ? params : null;
}

/** @param {MouseEvent} e */
function onLinkClick(e) {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  // Walk composedPath() so anchors inside Shadow DOM (e.g. inside <rm-list>'s
  // shadow tree) are still found. e.target is retargeted to the shadow host
  // at each shadow boundary, so the simple `e.target.closest("a[href]")`
  // doesn't see anchors below it.
  const path = typeof e.composedPath === "function" ? e.composedPath() : [];
  /** @type {HTMLAnchorElement | null} */
  let a = null;
  for (const n of path) {
    if (n instanceof HTMLAnchorElement) { a = n; break; }
  }
  if (!a) return;
  if (a.target && a.target !== "_self") return;
  const href = a.getAttribute("href");
  if (!href || href.startsWith("#") || href.startsWith("javascript:")) return;
  const url = new URL(a.href);
  if (url.origin !== location.origin) return;
  e.preventDefault();
  navigate(url.pathname);
}
